import React, { useContext, useEffect, useState } from 'react'
import clsx from 'clsx'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Layout from '../components/Layout'
import RechargingStationModal from '../components/RechargingStation/RechargingStationModal'
import CostsOverview from '../components/Costs/CostsOverview'
import { SUBSCRIPTION_TAB_HREF } from '../components/Costs/costsLinks'
import { UserContext } from '../contexts/UserContext'
import { PaymentContext } from '../contexts/PaymentContext'
import AuthService from '../services/AuthService'
import PaymentService from '../services/PaymentService'
import { useSettings } from '../hooks/useSettings'
import useNotification from '../hooks/useNotification'
import { performCentralLogout } from '../utils/centralLogout'
import { handoffUrl } from '../utils/vmHandoff'
import { finishRechargeFromVm, hasRechargeIntent } from '../utils/rechargeFromVm'
import { DEFAULT_LEARES_URL } from '../config/runtimeConfig'
import {
  getMainBackgroundClass,
  getTextClass,
  getBorderClass,
  getButtonClasses,
  getMutedTextClass,
} from '../utils/themeUtils'

// Landing autenticata del portale. Caso normale (VM assegnata): breve stato
// "Reindirizzamento…" e handoff automatico a {vm}/oauth-callback#token=.
// La dashboard (crediti, ricarica, logout) si mostra solo su /account o
// quando il redirect non è possibile (nessuna VM assegnata, o VM non
// disponibile). Una VM ancora in preparazione si aspetta qui, ricontrollando
// ogni VM_POLL_MS, invece di mandare l'utente su un dominio che non risponde.
const VM_POLL_MS = 15000

const DashboardPage = () => {
  const { t } = useTranslation('auth')
  const { user } = useContext(UserContext)
  const { isPaymentOpen, setIsPaymentOpen } = useContext(PaymentContext)
  const { currentTheme } = useSettings()
  const { error: showError } = useNotification()
  const location = useLocation()

  // /account (o ?stay=1) = l'utente vuole la dashboard, niente auto-redirect.
  // Idem se la scheda arriva da una VM per la stazione di ricarica: la VM
  // e' gia' aperta nell'altra scheda.
  const [fromVm] = useState(hasRechargeIntent)
  const stay =
    location.pathname === '/account' ||
    new URLSearchParams(location.search).has('stay') ||
    fromVm

  // Ritorno da Stripe Checkout, o arrivo da una VM: apre la stazione di ricarica
  useEffect(() => {
    const q = new URLSearchParams(location.search)
    // Ritorno da un acquisto (modulo o piano) o link «abbonati» della pagina Costi
    if (q.has('module_checkout') || q.has('plan_checkout') || q.has('station') || fromVm) {
      setIsPaymentOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  // Chiudere la stazione aperta da una VM chiude la scheda: si torna alla VM
  const closeOrSet = (open) => {
    if (!open) finishRechargeFromVm()
    setIsPaymentOpen(open)
  }

  const [credits, setCredits] = useState(null)
  const [assignedVm, setAssignedVm] = useState(undefined) // undefined = loading
  // ready | preparing | unavailable. Un backend che non lo manda vale ready.
  const [vmStatus, setVmStatus] = useState('ready')
  const [redirecting, setRedirecting] = useState(false)

  const loadVm = async (isAlive) => {
    try {
      const res = await AuthService.getAssignedVm()
      if (!isAlive()) return
      // La risposta del backend e' l'unica autorita': il suo default
      // (DEFAULT_LEARES_URL lato server) l'ha gia' applicato lui. Ricadere qui
      // sul default del bundle mandava gli account senza VM (e il loro token)
      // su un host dismesso — ticket 6aba6a34.
      setAssignedVm(res.data?.assigned_vm || null)
      setVmStatus(res.data?.vm_status || 'ready')
    } catch (e) {
      console.error('[Portal] getAssignedVm failed:', e?.response?.data)
      if (!isAlive()) return
      setAssignedVm(DEFAULT_LEARES_URL || null)
      setVmStatus('ready')
    }
  }

  useEffect(() => {
    let alive = true

    const load = async () => {
      try {
        const email =
          user?.email || sessionStorage.getItem('currentUserEmail') || ''
        if (email) {
          const res = await PaymentService.getCredits(email)
          if (alive) setCredits(res.data?.credits ?? res.data?.data?.credits)
        }
      } catch (e) {
        console.error('[Portal] getCredits failed:', e?.response?.data)
      }

      await loadVm(() => alive)
    }

    load()
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Finché la VM è in preparazione si richiede lo stato: quando diventa
  // ready l'effetto dell'handoff automatico parte da solo.
  useEffect(() => {
    if (vmStatus !== 'preparing') return
    let alive = true
    const timer = setInterval(() => loadVm(() => alive), VM_POLL_MS)
    return () => {
      alive = false
      clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vmStatus])

  const vmReady = Boolean(assignedVm) && vmStatus === 'ready'

  const handleEnterApp = async () => {
    if (!vmReady) {
      showError(t('portal.vmError'))
      return
    }
    setRedirecting(true)
    try {
      window.location.assign(await handoffUrl(assignedVm))
    } catch (e) {
      console.error('[Portal] handoff verso la VM fallito', e)
      setRedirecting(false)
      showError(t('portal.vmError'))
    }
  }

  // Caso normale: VM assegnata → handoff automatico dopo ~1s (l'utente vede
  // solo "Reindirizzamento…"). Escluso su /account e senza token.
  useEffect(() => {
    if (stay || redirecting) return
    if (!vmReady) return
    setRedirecting(true)
    const timer = setTimeout(async () => {
      try {
        window.location.assign(await handoffUrl(assignedVm))
      } catch (e) {
        console.error('[Portal] handoff automatico fallito', e)
        setRedirecting(false)
        showError(t('portal.vmError'))
      }
    }, 1000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedVm, vmStatus, stay])

  const handleLogout = async () => {
    await performCentralLogout()
    window.location.replace('/login')
  }

  // Nessun piano (spec weresp_deploy tier-registry 2026-10-22): la demo e' finita o il
  // piano pagato e' stato disdetto senza giorni di demo; in LeaRes e' sola lettura.
  // Admin e tester (tier_exempt) non ne sono limitati.
  const planInactive = user?.tier === 'inactive' && !user?.tier_exempt

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    ''

  // Flusso automatico (non /account): finché non sappiamo se esiste una VM
  // assegnata — e durante il redirect — niente dashboard, solo lo stato di
  // transizione (stesso stile di OAuthCallbackPage).
  if (!stay && (assignedVm === undefined || redirecting)) {
    return (
      <div
        className={clsx(
          'flex items-center justify-center min-h-screen',
          getMainBackgroundClass(currentTheme),
          getTextClass(currentTheme)
        )}
      >
        <div className="text-center" data-testid="portal-redirecting">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-current mx-auto mb-4"></div>
          <p className="text-lg">{t('portal.redirecting')}</p>
        </div>
      </div>
    )
  }

  if (!stay && vmStatus === 'preparing') {
    return (
      <div
        className={clsx(
          'flex items-center justify-center min-h-screen',
          getMainBackgroundClass(currentTheme),
          getTextClass(currentTheme)
        )}
      >
        <div className="text-center max-w-md px-4" data-testid="portal-vm-preparing">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-current mx-auto mb-4"></div>
          <p className="text-lg">{t('portal.vmPreparing')}</p>
          <button
            type="button"
            onClick={handleLogout}
            className={clsx('text-sm underline mt-6', getMutedTextClass(currentTheme))}
          >
            {t('portal.logout')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className={clsx(
        'w-screen h-screen',
        getMainBackgroundClass(currentTheme),
        getTextClass(currentTheme)
      )}
    >
      <Layout>
        <main className="h-[100svh] flex flex-col items-center justify-center gap-6">
          <img
            src="/assets/images/flowleap-logo.svg"
            alt="LeaRes"
            className="h-12 mb-2"
          />

          <div
            className={clsx(
              'w-[92%] max-w-md rounded-2xl border p-8 flex flex-col items-center gap-4',
              getBorderClass(currentTheme)
            )}
          >
            <h1 className="text-xl font-bold text-center">
              {t('portal.welcome', { name: displayName })}
            </h1>

            <p className={clsx('text-sm', getMutedTextClass(currentTheme))}>
              {t('portal.credits')}
            </p>
            <p className="text-4xl font-bold" data-testid="portal-credits">
              {credits === null ? '—' : Number(credits).toFixed(1)}
            </p>

            {planInactive && (
              <div
                role="status"
                data-testid="portal-plan-inactive"
                className={clsx(
                  'w-full rounded-xl border p-4 flex flex-col gap-3 text-sm text-center',
                  getBorderClass(currentTheme)
                )}
              >
                <p>{t('portal.planInactive')}</p>
                <button
                  type="button"
                  data-testid="portal-plan-inactive-subscribe"
                  onClick={() => setIsPaymentOpen(true)}
                  className={clsx(
                    'w-full h-[36px] rounded-xl text-white text-sm',
                    getButtonClasses(currentTheme, false)
                  )}
                >
                  {t('portal.planInactiveSubscribe')}
                </button>
                <a
                  href="/costs#costs-demo"
                  data-testid="portal-plan-inactive-costs"
                  className={clsx(
                    'underline-offset-4 hover:underline',
                    getMutedTextClass(currentTheme)
                  )}
                >
                  {t('portal.planInactiveCosts')}
                </a>
              </div>
            )}

            {assignedVm === null && (
              <p
                className="text-sm text-center text-red"
                data-testid="portal-no-vm"
              >
                {t('portal.noVm')}
              </p>
            )}

            {assignedVm && vmStatus === 'preparing' && (
              <p className="text-sm text-center" data-testid="portal-vm-preparing-note">
                {t('portal.vmPreparing')}
              </p>
            )}

            {assignedVm && vmStatus === 'unavailable' && (
              <p
                className="text-sm text-center text-red"
                data-testid="portal-vm-unavailable"
              >
                {t('portal.vmUnavailable')}
              </p>
            )}

            <button
              type="button"
              data-testid="portal-enter-app"
              onClick={handleEnterApp}
              disabled={!vmReady || redirecting}
              className={clsx(
                'w-full h-[40px] rounded-xl text-white text-sm taller:text-base',
                getButtonClasses(currentTheme, !vmReady || redirecting)
              )}
            >
              {redirecting ? t('portal.redirecting') : t('portal.enterApp')}
            </button>

            <button
              type="button"
              data-testid="portal-recharge"
              onClick={() => setIsPaymentOpen(true)}
              className={clsx(
                'w-full h-[40px] rounded-xl border text-sm taller:text-base hover:opacity-80 transition',
                getBorderClass(currentTheme)
              )}
            >
              {t('portal.recharge')}
            </button>

            {/* Plans and costs (ticket 6ad5d51d): what each plan, module and credit costs. */}
            <a
              href="/costs"
              data-testid="portal-costs-link"
              className={clsx(
                'text-sm underline-offset-4 hover:underline',
                getMutedTextClass(currentTheme)
              )}
            >
              {t('costs:nav.costsLink')}
            </a>

            <button
              type="button"
              data-testid="portal-logout"
              onClick={handleLogout}
              className={clsx(
                'text-sm underline mt-2',
                getMutedTextClass(currentTheme)
              )}
            >
              {t('portal.logout')}
            </button>
          </div>

          <div className="flex items-center gap-2 opacity-70">
            <span className="text-xs">powered by</span>
            <img
              src="/assets/images/medlea-logo.svg"
              alt="Medlea"
              className="h-4"
            />
          </div>
        </main>
      </Layout>

      {isPaymentOpen && (
        <RechargingStationModal
          setIsPaymentOpen={closeOrSet}
          // Plans, modules, credits and the demo, from the cart (ticket 6ad5d51d)
          extraTabs={[
            {
              id: 'Costs',
              label: t('costs:station.tab'),
              content: (
                <CostsOverview
                  id="station-costs"
                  variant="themed"
                  showIntro={false}
                  choosePlanHref={SUBSCRIPTION_TAB_HREF}
                />
              ),
            },
          ]}
        />
      )}
    </div>
  )
}

export default DashboardPage
